import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getById, getRelated, movies } from '@/mocks/movies';
import type { MovieItem } from '@/mocks/movies';

function MovieCard({ item }: { item: MovieItem }) {
  return (
    <Link
      to={`/article/${item.id}`}
      className="group block cursor-pointer"
      data-product-shop
    >
      <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-2 bg-background-200">
        <img
          src={item.image}
          alt={item.title}
          className="w-full h-full object-cover transition-transform duration-400 group-hover:scale-105"
          loading="lazy"
        />
        <div className="absolute top-2 left-2">
          <span className="px-1.5 py-0.5 text-[10px] rounded bg-primary-500/90 text-background-50 dark:text-foreground-950 whitespace-nowrap">
            {item.rating}
          </span>
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
        <div className="absolute bottom-2 left-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <span className="text-xs text-foreground-50 bg-primary-500/90 px-3 py-1.5 rounded-full flex items-center justify-center gap-1 whitespace-nowrap">
            <i className="ri-play-fill text-xs"></i> 观看
          </span>
        </div>
      </div>
      <h4 className="text-xs font-medium text-foreground-50 line-clamp-1 group-hover:text-primary-400 transition-colors">
        {item.title}
      </h4>
    </Link>
  );
}

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const item = getById(id || '');

  const related = useMemo(() => {
    if (!item) return [];
    return getRelated(item, 6);
  }, [item]);

  if (!item) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
            <i className="ri-error-warning-line text-2xl text-foreground-500"></i>
          </div>
          <h2 className="text-lg font-heading text-foreground-50 mb-2">内容未找到</h2>
          <p className="text-sm text-foreground-500 mb-4">抱歉，您查看的内容不存在或已下架</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-medium hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-home-line"></i> 返回首页
          </Link>
        </div>
      </div>
    );
  }

  const categoryLabelMap: Record<string, string> = {
    movie: '电影',
    tv: '电视剧',
    variety: '综艺',
    anime: '动漫',
  };

  const categoryLabel = categoryLabelMap[item.category] || item.category;

  // Breadcrumb JSON-LD
  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.bajieying.com/' },
      { '@type': 'ListItem', position: 2, name: categoryLabel, item: `https://www.bajieying.com/category/${item.category}` },
      { '@type': 'ListItem', position: 3, name: item.title },
    ],
  };

  // Article JSON-LD
  const articleLd = {
    '@context': 'https://schema.org',
    '@type': 'Movie',
    name: item.title,
    description: item.description,
    image: item.image,
    dateCreated: `${item.year}`,
    director: { '@type': 'Person', name: item.director },
    actor: item.actors.map((a) => ({ '@type': 'Person', name: a })),
    genre: item.genre,
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: item.rating,
      bestRating: '10',
      ratingCount: Math.floor(Math.random() * 100000) + 10000,
    },
    countryOfOrigin: { '@type': 'Country', name: item.region },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd) }}
      />

      <div className="pt-20">
        {/* Breadcrumb */}
        <div className="w-full px-4 md:px-6 pb-0">
          <div className="max-w-5xl mx-auto">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6">
              <Link to="/" className="hover:text-primary-400 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <Link to={`/category/${item.category}`} className="hover:text-primary-400 transition-colors cursor-pointer">{categoryLabel}</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-300">{item.title}</span>
            </nav>
          </div>
        </div>

        {/* Hero area */}
        <section className="w-full px-4 md:px-6 pb-10">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col md:flex-row gap-6 md:gap-8">
              {/* Poster */}
              <div className="w-full sm:w-56 md:w-64 flex-shrink-0">
                <div className="aspect-[3/4] rounded-lg overflow-hidden bg-background-200">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              {/* Info */}
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-3">
                  <span className="px-2 py-0.5 text-xs rounded-full bg-primary-500/90 text-background-50 dark:text-foreground-950 whitespace-nowrap">
                    {categoryLabel}
                  </span>
                  {item.isNew && (
                    <span className="px-2 py-0.5 text-xs rounded-full bg-accent-500/90 text-background-950 whitespace-nowrap">
                      NEW
                    </span>
                  )}
                  {item.isHot && (
                    <span className="px-2 py-0.5 text-xs rounded-full bg-secondary-500/90 text-background-50 dark:text-foreground-950 whitespace-nowrap">
                      热门
                    </span>
                  )}
                </div>

                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-50 mb-4">
                  {item.title}
                </h1>

                {/* Meta info */}
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-foreground-400 mb-5">
                  <div className="flex items-center gap-1">
                    <i className="ri-star-fill text-accent-500"></i>
                    <span className="text-foreground-50 font-semibold">{item.rating}</span>
                    <span className="text-foreground-600">/ 10</span>
                  </div>
                  <span>{item.year}</span>
                  <span>{item.region}</span>
                  <span>{item.genre.join(' / ')}</span>
                  <span className="text-accent-400">{item.updateInfo}</span>
                </div>

                {/* Director & Actors */}
                <div className="space-y-2 mb-5 text-sm">
                  <div className="flex items-start gap-2">
                    <span className="text-foreground-500 flex-shrink-0">导演：</span>
                    <span className="text-foreground-300">{item.director}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-foreground-500 flex-shrink-0">主演：</span>
                    <span className="text-foreground-300">{item.actors.join(' / ')}</span>
                  </div>
                </div>

                {/* Description */}
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-foreground-50 mb-2">剧情简介</h3>
                  <p className="text-sm text-foreground-400 leading-relaxed">
                    {item.description}
                  </p>
                </div>

                {/* Action buttons */}
                <div className="flex items-center gap-3">
                  <button className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-medium hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap">
                    <i className="ri-play-fill"></i>
                    立即播放
                  </button>
                  <button className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-background-100 text-foreground-300 text-sm font-medium hover:bg-background-200 hover:text-foreground-50 transition-all cursor-pointer whitespace-nowrap border border-background-200/60">
                    <i className="ri-heart-line"></i>
                    收藏
                  </button>
                  <button className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-background-100 text-foreground-300 text-sm font-medium hover:bg-background-200 hover:text-foreground-50 transition-all cursor-pointer whitespace-nowrap border border-background-200/60">
                    <i className="ri-share-forward-line"></i>
                    分享
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Related */}
        {related.length > 0 && (
          <section className="w-full px-4 md:px-6 py-10 bg-background-100">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-lg font-heading text-foreground-50 font-bold flex items-center gap-2">
                  <span className="w-1 h-5 bg-primary-500 rounded-full inline-block"></span>
                  相关推荐
                </h3>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3 md:gap-4">
                {related.map((r) => (
                  <MovieCard key={r.id} item={r} />
                ))}
              </div>
            </div>
          </section>
        )}
      </div>
    </>
  );
}